/**
 * Compact ordered pipeline row — separate bordered card (mockup spacing).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, GripVertical, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/atoms/Button';
import { Input } from '@/components/ui/atoms/Input';
import { Typography } from '@/components/ui/atoms/Typography';
import type { PipelineStage } from '@/models/job';
import { useStageDisplayLabel } from '@/hooks/useStageDisplayLabel';
import { StageAutomationCell } from './StageAutomationCell';
import { StageRowActions } from './StageRowActions';

/** Row layout grid for stage / automation / actions columns. */
const STAGE_ROW_GRID =
  'grid grid-cols-1 gap-3 lg:grid-cols-[18rem_minmax(0,1fr)_5.75rem] lg:items-center lg:gap-6';

export interface StageManagementRowProps {
  stage: PipelineStage;
  candidateCount: number;
  automationEnabled: boolean;
  onAutomationChange: (stageId: string, enabled: boolean) => void;
  onRename: (stageId: string, label: string) => void;
  onDelete?: (stageId: string) => void;
  canDelete?: boolean;
  isRenaming?: boolean;
  showDropIndicatorAbove?: boolean;
  showDropIndicatorBelow?: boolean;
}

export function StageManagementRow({
  stage,
  candidateCount,
  automationEnabled,
  onAutomationChange,
  onRename,
  onDelete,
  canDelete = false,
  isRenaming = false,
  showDropIndicatorAbove = false,
  showDropIndicatorBelow = false,
}: StageManagementRowProps) {
  const { t } = useTranslation(['job', 'common']);
  const label = useStageDisplayLabel()(stage);
  const [isEditing, setIsEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(label);

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stage.id });

  useEffect(() => {
    setDraftLabel(label);
  }, [label]);

  const commitRename = () => {
    const trimmed = draftLabel.trim();
    // Saving the displayed name unchanged must not make a default stage user-owned.
    if (!trimmed || trimmed === label) {
      setDraftLabel(label);
      setIsEditing(false);
      return;
    }
    onRename(stage.id, trimmed);
    setIsEditing(false);
  };

  const cancelRename = () => {
    setDraftLabel(label);
    setIsEditing(false);
  };

  const actions = (
    <StageRowActions
      stageLabel={label}
      isDefault={stage.isDefault}
      canDelete={canDelete}
      isEditing={isEditing}
      isRenaming={isRenaming}
      onRename={() => setIsEditing(true)}
      onDelete={onDelete ? () => onDelete(stage.id) : undefined}
    />
  );

  return (
    <li className="relative list-none">
      {showDropIndicatorAbove && (
        <div
          className="pointer-events-none absolute inset-x-2 -top-1.5 z-20 h-0.5 rounded-full bg-purple"
          aria-hidden
        />
      )}

      <div
        ref={setNodeRef}
        style={{
          transform: CSS.Translate.toString(transform),
          transition,
        }}
        className={`${STAGE_ROW_GRID} rounded-xl border border-light-lila/70 bg-light px-4 py-3 ${
          isDragging ? 'z-10 opacity-40 shadow-md' : ''
        }`}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <Button
            ref={setActivatorNodeRef}
            type="icon"
            intent="muted"
            size="sm"
            icon={<GripVertical />}
            aria-label={t('job:stage_management.reorder_aria', {
              name: label,
            })}
            className="!cursor-grab active:!cursor-grabbing touch-none shrink-0"
            {...attributes}
            {...listeners}
          />

          <div className="min-w-0 flex-1">
            {isEditing ? (
              <div className="flex max-w-sm items-center gap-1.5">
                <Input
                  value={draftLabel}
                  onChange={(event) => setDraftLabel(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      commitRename();
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      cancelRename();
                    }
                  }}
                  disabled={isRenaming}
                  autoFocus
                  aria-label={t('job:stage_management.name_aria')}
                  size="sm"
                  className="min-w-0 flex-1"
                />
                <Button
                  type="icon"
                  intent="primary"
                  size="sm"
                  icon={<Check />}
                  aria-label={t('common:actions.save', {
                    defaultValue: 'Save',
                  })}
                  disabled={isRenaming || !draftLabel.trim()}
                  onClick={commitRename}
                />
                <Button
                  type="icon"
                  intent="muted"
                  size="sm"
                  icon={<X />}
                  aria-label={t('common:actions.cancel', {
                    defaultValue: 'Cancel',
                  })}
                  disabled={isRenaming}
                  onClick={cancelRename}
                />
              </div>
            ) : (
              <>
                <Typography
                  as="p"
                  variant="body-sm"
                  weight="semibold"
                  color="primary"
                  className="truncate leading-snug"
                >
                  {label}
                </Typography>
                <Typography
                  as="p"
                  variant="body-xs"
                  color="muted"
                  className="mt-0.5 flex items-center gap-1.5"
                >
                  <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {t('job:stage_management.candidates_count', {
                    count: candidateCount,
                  })}
                </Typography>
              </>
            )}
          </div>

          <div className="lg:hidden">{actions}</div>
        </div>

        <div className="min-w-0 pl-10 lg:pl-0">
          <StageAutomationCell
            stageType={stage.stageType}
            hasAutomationRule={stage.hasAutomationRule}
            enabled={automationEnabled}
            onEnabledChange={(next) => onAutomationChange(stage.id, next)}
          />
        </div>

        <div className="hidden justify-end lg:flex">{actions}</div>
      </div>

      {showDropIndicatorBelow && (
        <div
          className="pointer-events-none absolute inset-x-2 -bottom-1.5 z-20 h-0.5 rounded-full bg-purple"
          aria-hidden
        />
      )}
    </li>
  );
}
